/**
 * EnvSynthesisBar — bandeau de 4 KPIs en haut de l'onglet Environnement.
 *
 * L'idée : donner en un coup d'œil les 4 métriques que l'utilisateur
 * regarde le plus souvent avant de scroller vers les cartes détaillées.
 *
 *   1. École la plus proche         → distance min tous types confondus
 *   2. Commerces walkables          → nb de commerces dans le rayon courant
 *   3. DPE médiane du quartier      → lettre dominante + total
 *   4. Couverture fibre commune     → % fibre (ARCEP)
 *
 * Réutilise les mêmes hooks que les cartes — grâce à la dedup in-flight
 * dans `useEnvBlock` (cf. shared.ts), l'affichage du bandeau + des cartes
 * ne déclenche qu'une seule lecture Firestore et un seul fetch upstream
 * par bloc, même au premier rendu.
 *
 * Tolérance aux pannes : chaque KPI gère son état indépendamment —
 * si ARCEP plante mais que DPE répond, on affiche 3 KPIs + 1 tiret.
 * Toute l'UX est "progressive" ; pas de spinner global.
 */

import type { FicheDoc } from "../../lib/fiches";
import { useEcoles } from "../../lib/env/ecoles";
import { useCommerces } from "../../lib/env/commerces";
import { useDpe } from "../../lib/env/dpe";
import { useArcep } from "../../lib/env/arcep";
import { Card } from "../ui";
import { Ic } from "../icons";
import type { ReactNode } from "react";

type Props = { fiche: FicheDoc };

export function EnvSynthesisBar({ fiche }: Props) {
  const ecoles = useEcoles(fiche.id, fiche.geocode);
  const commerces = useCommerces(fiche.id, fiche.geocode);
  const dpe = useDpe(fiche.id, fiche.geocode);
  const arcep = useArcep(fiche.id, fiche.geocode);

  // ── KPI 1 : école la plus proche ────────────────────────────────
  const closestSchool =
    ecoles.status === "ready" && ecoles.data.items.length > 0
      ? ecoles.data.items[0]
      : null;
  const kpiSchool: Kpi = {
    icon: <Ic.Users size={16} />,
    label: "École la plus proche",
    primary: closestSchool ? formatDistance(closestSchool.distance_m) : "—",
    secondary: closestSchool ? truncate(closestSchool.name, 38) : placeholderOf(ecoles.status),
    tone: closestSchool
      ? closestSchool.distance_m <= 600
        ? "good"
        : closestSchool.distance_m <= 1500
        ? "ok"
        : "warn"
      : "muted",
  };

  // ── KPI 2 : commerces accessibles ───────────────────────────────
  // Sum of alimentation + restauration + services + santé within the
  // tier-adaptive radius. We treat "8+ commerces" as the threshold for
  // "vraiment bien desservi" — empirically, a block with 8 diverse shops
  // in walking distance covers all weekly errands.
  const commercesTotal =
    commerces.status === "ready" ? commerces.data.items.length : null;
  const kpiCommerces: Kpi = {
    icon: <Ic.Wallet size={16} />,
    label: "Commerces à proximité",
    primary:
      commercesTotal !== null
        ? commercesTotal === 0
          ? "0"
          : `${commercesTotal}`
        : "—",
    secondary:
      commerces.status === "ready"
        ? `rayon ${formatRadius(commerces.data.radius_m)}`
        : placeholderOf(commerces.status),
    tone:
      commercesTotal === null
        ? "muted"
        : commercesTotal >= 20
        ? "good"
        : commercesTotal >= 8
        ? "ok"
        : commercesTotal > 0
        ? "warn"
        : "bad",
  };

  // ── KPI 3 : DPE médiane ──────────────────────────────────────────
  const kpiDpe: Kpi = {
    icon: <Ic.Bolt size={16} />,
    label: "DPE médiane quartier",
    primary: dpe.status === "ready" && dpe.data.median ? dpe.data.median : "—",
    secondary:
      dpe.status === "ready"
        ? dpe.data.total === 0
          ? "aucun diagnostic"
          : `${dpe.data.total >= 200 ? "200+" : dpe.data.total} diagnostics`
        : placeholderOf(dpe.status),
    tone:
      dpe.status !== "ready" || !dpe.data.median
        ? "muted"
        : ["A", "B"].includes(dpe.data.median)
        ? "good"
        : ["C", "D"].includes(dpe.data.median)
        ? "ok"
        : ["E"].includes(dpe.data.median)
        ? "warn"
        : "bad",
  };

  // ── KPI 4 : fibre commune ────────────────────────────────────────
  const kpiArcep: Kpi = {
    icon: <Ic.Cpu size={16} />,
    label: "Éligibilité fibre commune",
    primary:
      arcep.status === "ready" ? formatPct(arcep.data.fiber_pct) : "—",
    secondary:
      arcep.status === "ready"
        ? arcep.data.fiber_pct >= 95
          ? "couverture intégrale"
          : "source ARCEP"
        : placeholderOf(arcep.status),
    tone:
      arcep.status !== "ready"
        ? "muted"
        : arcep.data.fiber_pct >= 95
        ? "good"
        : arcep.data.fiber_pct >= 50
        ? "ok"
        : arcep.data.fiber_pct > 0
        ? "warn"
        : "bad",
  };

  return (
    <Card className="p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile kpi={kpiSchool} />
        <KpiTile kpi={kpiCommerces} />
        <KpiTile kpi={kpiDpe} />
        <KpiTile kpi={kpiArcep} />
      </div>
    </Card>
  );
}

// ─── Tile ────────────────────────────────────────────────────────────────

type Tone = "good" | "ok" | "warn" | "bad" | "muted";
type Kpi = {
  icon: ReactNode;
  label: string;
  primary: string;
  secondary: string;
  tone: Tone;
};

// Dot + subtle tinted pill : we avoid colouring the background of the whole
// tile to keep the bar readable as a row of small numbers. The dot carries
// the semantic, the number stays in the default ink colour.
const TONE_DOT: Record<Tone, string> = {
  good: "bg-emerald-500",
  ok: "bg-lime-400",
  warn: "bg-amber-400",
  bad: "bg-rose-500",
  muted: "bg-ink-300 animate-pulse",
};

function KpiTile({ kpi }: { kpi: Kpi }) {
  return (
    <div className="rounded-xl bg-ink-50 hairline px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.08em] text-ink-500">
        <span className="text-ink-400">{kpi.icon}</span>
        <span className="truncate">{kpi.label}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span
          className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[kpi.tone]} self-center`}
        />
        <span className="text-[20px] font-semibold tabular leading-tight text-ink-900">
          {kpi.primary}
        </span>
      </div>
      <div className="text-[11px] text-ink-500 mt-0.5 truncate">
        {kpi.secondary}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function placeholderOf(status: string): string {
  if (status === "error") return "source indispo";
  return "chargement…";
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

function formatDistance(m: number): string {
  if (m < 950) return `${m} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

function formatRadius(m: number): string {
  if (m < 1000) return `${m} m`;
  if (m % 1000 === 0) return `${m / 1000} km`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

function formatPct(p: number): string {
  if (p >= 100) return "100 %";
  if (p <= 0) return "0 %";
  if (p >= 10) return `${Math.round(p)} %`;
  return `${p.toFixed(1).replace(".", ",")} %`;
}
